import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { DocumentCard } from '@zorro/design-system/v2/DocumentCard';
import { formatDateEnLocale, getNow } from '@zorro/shared/formatters';
import { FormErrorMessage, Stack, Text } from '@zorro/web/design-system';
import { downloadBenefitDocument } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../../../Analytics';
import {
  UploadableBenefitDocument,
  getDocumentKey,
  isPersistedDocument,
} from '../../../BenefitDocuments';
import { DocumentListSection } from '../../../DocumentListSection';
import { CarrierApplicationSubmitFormFields } from '../CarrierCompleteStepUtils';

type Props = {
  onboardingPeriodId: string;
  isEnrollConnectSubmitted?: boolean;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
};

export const CarrierApplicationEvidenceSection = ({
  onboardingPeriodId,
  isEnrollConnectSubmitted = false,
  submissionForm,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = submissionForm;
  const applicationEvidences = watch('applicationEvidences');
  const noApplicationEvidence = watch('noApplicationEvidence');

  const handleDropEvidence = (files: File[]) => {
    const currentEvidences = Array.isArray(applicationEvidences)
      ? applicationEvidences
      : [];
    setValue('applicationEvidences', [...currentEvidences, ...files], {
      shouldValidate: true,
      shouldDirty: true,
    });
    void sendEvent('upload_submission_evidence', {
      onboarding_period_id: onboardingPeriodId,
      number_of_files: files.length,
    });
  };

  const handleDeleteEvidence = (evidence: UploadableBenefitDocument) => {
    const currentEvidences = Array.isArray(applicationEvidences)
      ? applicationEvidences
      : [];
    setValue(
      'applicationEvidences',
      currentEvidences.filter((item) => item !== evidence),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const handleDownloadEvidence = async (
    document: Exclude<UploadableBenefitDocument, File>
  ) => downloadBenefitDocument(document.documentUrl, document.fileName);

  const hasEvidences =
    Array.isArray(applicationEvidences) && applicationEvidences.length > 0;

  const evidenceList =
    Array.isArray(applicationEvidences) &&
    applicationEvidences.map((evidence) => (
      <DocumentCard
        key={getDocumentKey(evidence)}
        fileName={evidence instanceof File ? evidence.name : evidence.fileName}
        variant="shadow"
        date={formatDateEnLocale(
          isPersistedDocument(evidence) ? evidence.createdAt : getNow()
        )}
        onDownload={
          evidence instanceof File
            ? undefined
            : () => handleDownloadEvidence(evidence)
        }
        onDelete={() => handleDeleteEvidence(evidence)}
      />
    ));

  return (
    <Stack my="xl" gap="xxs">
      <Text w="100%" size="md" fw={600}>
        {isEnrollConnectSubmitted
          ? 'Submission evidence'
          : 'Upload submission evidence'}
      </Text>
      <Stack gap="sm">
        {!isEnrollConnectSubmitted && (
          <Text size="sm" c="var(--color-text-default-secondary)">
            Upload screenshots, email confirmations, or any other application
            evidence
          </Text>
        )}
        <DocumentListSection
          canManage={!noApplicationEvidence}
          isEmpty={!hasEvidences}
          isMultiple
          onUpload={handleDropEvidence}
          uploadLabel="Upload submission evidence"
          readOnlyEmptySubtitle="No application evidence"
        >
          {evidenceList}
        </DocumentListSection>
        {!noApplicationEvidence && (
          <FormErrorMessage fieldName="applicationEvidences" errors={errors} />
        )}
        {!(isEnrollConnectSubmitted && hasEvidences) && (
          <Controller
            control={control}
            name="noApplicationEvidence"
            render={({ field: { value, ...rest } }) => (
              <Checkbox
                {...rest}
                checked={Boolean(value)}
                label="No application evidence"
                disabled={hasEvidences}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};
